import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { GripVertical, Loader2 } from 'lucide-react';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';
import { BASE_SETTINGS_URL, isAdminRole } from '../../components/constants';
import { getAllTasks } from '../../Api/taskApi';
import { authHeaders } from '../../utils/authHeaders';
import { compareAgentOrder } from '../../utils/agentOrder';

const PLACEMENT_KEYS = ['agentOrder.posTopLeft', 'agentOrder.posTopRight', 'agentOrder.posSecondLeft', 'agentOrder.posSecondRight'];

const sameId = (a, b) => a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

const AgentOrder = ({ organizationId, departments: departmentsProp, departmentsLoading, addToast, onDirtyChange }) => {
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { data } = useSelector((state) => state.admin);

  const [activeDeptId, setActiveDeptId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksError, setTasksError] = useState(false);
  const [order, setOrder] = useState({});
  const [savedOrder, setSavedOrder] = useState({});
  const [positionDrafts, setPositionDrafts] = useState({});
  const [tasksLoading, setTasksLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const draggedIndexRef = useRef(null);
  const tasksRequestRef = useRef(0);

  const canManage = isAdminRole(data?.user_role);

  const departments = useMemo(
    () => [...(departmentsProp || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [departmentsProp],
  );

  const loadTasks = () => {
    const requestId = ++tasksRequestRef.current;
    return getAllTasks(null, organizationId)
      .then((res) => {
        if (requestId !== tasksRequestRef.current) return;
        setTasks(res?.tasks || []);
        setTasksError(false);
      })
      .catch(() => {
        if (requestId === tasksRequestRef.current) setTasksError(true);
      })
      .finally(() => {
        if (requestId === tasksRequestRef.current) setTasksLoading(false);
      });
  };

  useEffect(() => {
    if (!canManage || !organizationId) {
      setTasksLoading(false);
      return;
    }
    setTasksLoading(true);
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, organizationId]);

  useEffect(() => {
    if (!departments.some((d) => d.id === activeDeptId)) setActiveDeptId(departments[0]?.id ?? null);
  }, [departments, activeDeptId]);

  useEffect(() => {
    const next = {};
    departments.forEach((dept) => {
      next[dept.id] = tasks
        .filter((task) => task.name !== '__CHAT_INTERNAL__'
          && sameId(task.organization_id, organizationId)
          && sameId(task.department_id, dept.id))
        .sort(compareAgentOrder)
        .map((task) => task.id);
    });
    setOrder(next);
    setSavedOrder(next);
    setPositionDrafts({});
  }, [departments, tasks, organizationId]);

  const taskById = useMemo(() => {
    const map = {};
    tasks.forEach((task) => { map[task.id] = task; });
    return map;
  }, [tasks]);

  const changedDeptIds = Object.keys(order).filter(
    (deptId) => (order[deptId] || []).join(',') !== (savedOrder[deptId] || []).join(','),
  );
  const hasChanges = changedDeptIds.length > 0;
  const activeIds = order[activeDeptId] || [];
  const loading = tasksLoading || departmentsLoading;

  useEffect(() => {
    onDirtyChange?.(hasChanges);
    if (!hasChanges) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const moveTo = (fromIndex, toIndex) => {
    if (saving || fromIndex === toIndex) return;
    setOrder((prev) => {
      const list = [...(prev[activeDeptId] || [])];
      const [moved] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, moved);
      return { ...prev, [activeDeptId]: list };
    });
  };

  const handleDragStart = (e, index) => {
    if (saving) {
      e.preventDefault();
      return;
    }
    draggedIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    const from = draggedIndexRef.current;
    if (from === null || from === index) return;
    moveTo(from, index);
    draggedIndexRef.current = index;
  };

  const handleDragEnd = () => { draggedIndexRef.current = null; };

  const commitPosition = (taskId) => {
    const raw = positionDrafts[taskId];
    setPositionDrafts((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    if (saving || raw === undefined || raw === '') return;
    setOrder((prev) => {
      const list = [...(prev[activeDeptId] || [])];
      const from = list.indexOf(taskId);
      if (from === -1) return prev;
      const target = Math.min(Math.max(parseInt(raw, 10) || 1, 1), list.length) - 1;
      if (target === from) return prev;
      list.splice(from, 1);
      list.splice(target, 0, taskId);
      return { ...prev, [activeDeptId]: list };
    });
  };

  const handleCancel = () => {
    setOrder(savedOrder);
    setPositionDrafts({});
  };

  const handleSave = async () => {
    const payload = changedDeptIds.flatMap((deptId) =>
      order[deptId].map((taskId, idx) => ({ task_id: Number(taskId), position: idx + 1 })),
    );
    setSaving(true);
    try {
      await axios.put(`${BASE_SETTINGS_URL}/tasks/order`, payload, authHeaders());
      await loadTasks();
      addToast(t('agentOrder.toastSaved'), 'success');
    } catch (err) {
      addToast(t(err?.response?.status === 403 ? 'agentOrder.toastNoPermission' : 'agentOrder.toastError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) return null;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl">
      <div className="flex items-center justify-between gap-4 px-8 py-4">
        <p className="text-[14px] text-[#6B7280]">{t('agentOrder.hint')}</p>
        <div className="flex items-center gap-3 flex-shrink-0">
          {hasChanges && (
            <span className="text-[14px] text-[#6B7280]">{t('agentOrder.unsavedChanges')}</span>
          )}
          <button
            type="button"
            onClick={handleCancel}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('agentOrder.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-4 py-2 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center"
            style={{ backgroundColor: aispaceColor }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : t('agentOrder.save')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 border-t border-[#E5E7EB]">
          <Loader2 size={22} className="animate-spin" style={{ color: aispaceColor }} />
        </div>
      ) : tasksError ? (
        <div className="px-6 py-12 text-center border-t border-[#E5E7EB]">
          <p className="text-[14px] text-[#DC2626] mb-4">{t('agentOrder.loadError')}</p>
          <button
            type="button"
            onClick={loadTasks}
            className="px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('agentOrder.retry')}
          </button>
        </div>
      ) : departments.length === 0 ? (
        <div className="px-6 py-12 text-center text-[14px] text-[#9CA3AF] border-t border-[#E5E7EB]">{t('agentOrder.noDepartments')}</div>
      ) : (
        <>
          <div className="flex items-center gap-7 px-8 border-t border-b border-[#E5E7EB] pt-3 overflow-x-auto thin-scrollbar">
            {departments.map((dept) => {
              const isActive = dept.id === activeDeptId;
              return (
                <button
                  key={dept.id}
                  type="button"
                  onClick={() => { setActiveDeptId(dept.id); setPositionDrafts({}); }}
                  className={`pb-3 text-[14px] font-medium transition-colors relative whitespace-nowrap ${isActive ? '' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}
                  style={{ color: isActive ? aispaceColor : undefined }}
                >
                  {dept.department_name}
                  {changedDeptIds.includes(String(dept.id)) && (
                    <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full align-middle" style={{ backgroundColor: aispaceColor }} />
                  )}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: aispaceColor }} />
                  )}
                </button>
              );
            })}
          </div>

          {activeIds.length === 0 ? (
            <div className="px-6 py-12 text-center text-[14px] text-[#9CA3AF]">{t('agentOrder.noAgents')}</div>
          ) : (
            <div>
              <div className="flex items-center px-8 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] text-[13px] font-medium text-[#6B7280]">
                <div className="w-[28px] mr-2" />
                <div className="w-[88px]">{t('agentOrder.colPosition')}</div>
                <div className="flex-[2]">{t('agentOrder.colAgent')}</div>
                <div className="flex-1">{t('agentOrder.colPlacement')}</div>
              </div>
              {activeIds.map((taskId, index) => {
                const task = taskById[taskId];
                if (!task) return null;
                const draft = positionDrafts[taskId];
                return (
                  <div
                    key={taskId}
                    draggable={!saving}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center px-8 py-4 hover:bg-[#FAFAFA] transition-colors ${saving ? 'opacity-60 cursor-wait' : 'cursor-grab active:cursor-grabbing'} ${index !== activeIds.length - 1 ? 'border-b border-[#E5E7EB]' : ''}`}
                  >
                    <div className="w-[28px] flex-shrink-0 mr-2 text-[#D1D5DB] hover:text-[#9CA3AF]">
                      <GripVertical size={18} />
                    </div>
                    <div className="w-[88px] flex-shrink-0">
                      <input
                        type="number"
                        min={1}
                        max={activeIds.length}
                        disabled={saving}
                        aria-label={t('agentOrder.colPosition')}
                        value={draft !== undefined ? draft : index + 1}
                        onChange={(e) => setPositionDrafts((prev) => ({ ...prev, [taskId]: e.target.value }))}
                        onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; commitPosition(taskId); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        onFocus={(e) => { e.target.style.borderColor = aispaceColor; }}
                        className="w-[60px] px-2 py-1.5 text-[14px] text-center text-[#111827] border border-[#E5E7EB] rounded-lg outline-none transition-colors"
                      />
                    </div>
                    <div className="flex-[2] min-w-0 pr-4 text-[14px] font-medium text-[#111827] truncate">
                      {task.name}
                    </div>
                    <div className="flex-1 min-w-0">
                      {index < PLACEMENT_KEYS.length ? (
                        <span className="inline-block px-3 py-1 text-[12px] font-medium rounded-full" style={{ color: aispaceColor, backgroundColor: `${aispaceColor}14` }}>
                          {t(PLACEMENT_KEYS[index])}
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1 text-[12px] font-medium text-[#6B7280] bg-[#F3F4F6] rounded-full">
                          {t('agentOrder.posBehindShowAll')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgentOrder;
